import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { Country } from '../country';
import { HttpService } from '../service/http.service';
import { Language } from '../language';
import { ProgrammeInformation } from '../programme-information';
import { ProgrammeType } from '../programme-type';
import { SchoolContactType } from '../school-contact-type';
import { SchoolWithDetail } from '../school-with-detail';
import { SelectItemGroup } from 'primeng/api';
import { SchoolComment } from '../school-comment';

@Injectable({
  providedIn: 'root'
})
export class SchoolService {

  public countries: Country[] = [];
  public programmeTypes: ProgrammeType[] = [];

  public selectedSchool!: SchoolWithDetail;
  public selectedSchoolId!: number;

  public itemGroups: SelectItemGroup[] = [];
  public schoolForm!: FormGroup;
  public commentForm!: FormGroup;
  public languages: Language[] = [];

  public comments: SchoolComment[] = [];

  public schoolContactTypes: SchoolContactType[] = [];

  updatingSchool = false;

  constructor(
    private builder: FormBuilder,
    private httpService: HttpService
  ) { }

  initCourseForm() {
    let courses = this.schoolForm.get("courses") as FormArray;
    courses.clear();
    for (let course of this.selectedSchool.courseList) {
      courses.push(this.builder.group({
        id: course.id,
        code: course.code,
        description: course.descriptionFr,
        created: course.created,
        createdBy: course.createdBy,
        descriptionEn: [course.descriptionEn, Validators.required],
        descriptionNl: course.descriptionNl,
        descriptionEs: course.descriptionEs,
        descriptionPt: course.descriptionPt
      }));
    }
  }

  initCountriesForm() {
    let countries = this.schoolForm.get("countries") as FormArray;
    countries.clear();
    for (let country of this.selectedSchool.countryList) {
      countries.push(new FormControl(country));
    }
    if (this.selectedSchool.countryList.length == 0) {
      countries.push(new FormControl(null));
    }
  }
  
  initCommentForm() {
    this.commentForm = this.builder.group({
      title: ["", Validators.required],
      body: ["", Validators.required]
    });
  }

  getComments() {
    this.httpService.getSchoolCommentsBySchoolId(this.selectedSchool.id!).subscribe(res => {
      this.comments = res;
    });
  }
  
  initProgrammeTypesForm() {
    let programmeTypes = this.schoolForm.get("programmeTypes") as FormArray;
    programmeTypes.clear();
    for (let programmeType of this.selectedSchool.programmeTypes) {
      programmeTypes.push(new FormControl(programmeType));
    }
    if (this.selectedSchool.programmeTypes.length == 0) {
      programmeTypes.push(new FormControl(null));
    }
  }

  initProgrammeInformationsForm(){
    let programmeInformations = this.schoolForm.get("programmeInformations") as FormArray;
    programmeInformations.clear();
    let languageIds = [];
    for(let programme of this.selectedSchool.programmeInformationList){
      programmeInformations.controls.push(this.builder.group({
        name: programme.name,
        id: programme.id, 
        lviSiteUrl: programme.lviSiteUrl,
        language: programme.language,
        label: [programme.label, Validators.max(7)]
      }));
      // if language is new, push in languageIds
      if(languageIds.indexOf(programme.language.id!)==-1){
        languageIds.push(programme.language.id);
      }
    }

    if(languageIds.length < this.languages.length){
      while(languageIds.length < this.languages.length){
        let newLanguage = this.getNewLanguage(languageIds);
        programmeInformations.controls.push(this.builder.group({
          name: null,
          id: null, 
          lviSiteUrl: [null, Validators.required],
          language: newLanguage,
          label: [null, Validators.max(7)]
        }));
        languageIds.push(newLanguage.id);
      }
    }
  }

  getNewLanguage(languageIds: number[]){
    for(let language of this.languages){
      let isNewLanguage = true;
      for(let languageId of languageIds){
        if(languageId == language.id){
          isNewLanguage = false;
        }
      }
      if(isNewLanguage){
        return language;
      }
    }
    // if all the languages are used already, return french
    return this.languages[0];
  }

  updateProgrammeInformations(){
    let programmeInformations = this.schoolForm.get("programmeInformations") as FormArray;
    let newProgrammeInformations = [];
    for(let form of programmeInformations.controls){
      if(form.valid){
        newProgrammeInformations.push(new ProgrammeInformation(
          form.value.id,
          form.value.name,
          form.value.language,
          form.value.lviSiteUrl,
          form.value.label
        ));
      }
    }
    this.selectedSchool.programmeInformationList = newProgrammeInformations;
  }

  pushProgrammeInformationForm(){
    let programmeInformations = this.schoolForm.get("programmeInformations") as FormArray;
    programmeInformations.controls.push(this.builder.group({
      name: null,
      id: null, 
      lviSiteUrl: [null, Validators.required],
      language: this.languages[0],
      label: [null, Validators.max(7)]
    }));
  }

}
